import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useQuickMatch } from '../hooks/matchmaking/useQuickMatch';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Search, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';

// Array of background images for the animation
const BACKGROUND_IMAGES = [
  'https://images.unsplash.com/photo-1614427771426-50fa133eebcf?auto=format&fit=crop&w=1600&q=80',
  'https://images.unsplash.com/photo-1728230569861-8839f8009693?auto=format&fit=crop&w=1600&q=80',  
  'https://images.unsplash.com/photo-1736117705769-8486f24ed58a?auto=format&fit=crop&w=1600&q=80',  
  'https://images.unsplash.com/photo-1665223471595-4077d43dc5b7?auto=format&fit=crop&w=1600&q=80',  
  'https://images.unsplash.com/photo-1498354178607-a79df2916198?auto=format&fit=crop&w=1600&q=80',  
  'https://images.unsplash.com/photo-1660214402503-50db22c94a95?auto=format&fit=crop&w=1600&q=80', 
  'https://images.unsplash.com/photo-1582146478225-979814b1d093?auto=format&fit=crop&w=1600&q=80', 
  'https://images.unsplash.com/photo-1587400873582-230980eb46eb?auto=format&fit=crop&w=1600&q=80', 
  'https://images.unsplash.com/photo-1521217078329-f8fc1becab68?auto=format&fit=crop&w=1600&q=80',  
];

export default function QuickPlayTransition() {
  const navigate = useNavigate();
  const { resetSearch } = useQuickMatch();
  const [backgroundImage, setBackgroundImage] = React.useState('');
  const [playerAvatars] = React.useState([
    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&q=80',
    'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&w=150&q=80',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&q=80',
    'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=150&q=80',
    'https://images.unsplash.com/photo-1722270608841-35d7372a2e85?auto=format&fit=crop&w=150&q=80',
    'https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?auto=format&fit=crop&w=150&q=80',
  ]);

  // Add CSS for animations
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes wave-out {
        0% { transform: scale(0.5); opacity: 0.7; }
        100% { transform: scale(3); opacity: 0; }
      }
      
      @keyframes radar-sweep {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      @keyframes pulse-scale {
        0% { transform: scale(1); }
        50% { transform: scale(1.15); }
        100% { transform: scale(1); }
      }
      
      @keyframes float {
        0% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
        100% { transform: translateY(0px); }
      }
      
      @keyframes glow {
        0% { box-shadow: 0 0 5px rgba(255, 201, 51, 0.5); }
        50% { box-shadow: 0 0 20px rgba(255, 201, 51, 0.8); }
        100% { box-shadow: 0 0 5px rgba(255, 201, 51, 0.5); }
      }
      
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      @keyframes map-pan {
        0% { transform: translate(-5%, -5%); }
        50% { transform: translate(5%, 5%); }
        100% { transform: translate(-5%, -5%); }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Select a random background image when component mounts
  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * BACKGROUND_IMAGES.length);
    setBackgroundImage(BACKGROUND_IMAGES[randomIndex]);
  }, []);

  useEffect(() => {
    resetSearch();
    const timer = setTimeout(() => {
      navigate('/dashboard/quick-play', { 
        replace: true,
        state: { fromNav: true, searchKey: Date.now() }
      });
    }, 3500); // Increased to 3.5 seconds to show the enhanced animation
    return () => clearTimeout(timer);
  }, [navigate, resetSearch]);

  return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-[calc(100vh-8rem)] overflow-hidden absolute inset-0 top-0 bottom-16">
        <motion.div 
          className="backdrop-blur-md bg-dark-200/40 rounded-2xl p-8 w-full h-full mx-auto text-center relative overflow-hidden flex flex-col justify-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Map background with subtle movement */}
          <div className="absolute inset-0 opacity-15 overflow-hidden">
            <div 
              className="w-[120%] h-[120%]"
              style={{
                backgroundImage: `url('${backgroundImage}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                animation: "map-pan 20s ease-in-out infinite"
              }}
            />
          </div>

          {/* Radar animation container */}
          <div className="relative flex items-center justify-center h-[50vh] w-full mx-auto">
            {/* Concentric circles */}
            {[0, 1, 2, 3].map((i) => (
              <div 
                key={`circle-${i}`}
                className={cn(
                  "absolute rounded-full border-2",
                  i % 2 === 0 ? "border-gold-400/30" : "border-emerald-500/30"
                )}
                style={{ 
                  width: `${(i+1) * 80}px`, 
                  height: `${(i+1) * 80}px`,
                  animation: `pulse-scale 3s ease-in-out infinite ${i * 0.5}s`
                }}
              />
            ))}
            
            {/* Pulsing waves */}
            {[0, 1, 2].map((i) => (
              <div 
                key={`wave-${i}`}
                className={cn(
                  "absolute rounded-full",
                  i % 3 === 0 ? "bg-gold-400/10" : 
                  i % 3 === 1 ? "bg-emerald-500/10" : "bg-blue-500/10"
                )}
                style={{ 
                  animation: `wave-out 4s ease-out infinite ${i * 1.2}s`,
                }}
              />
            ))}
            
            {/* Radar sweeps */}
            <div className="absolute w-48 h-48">
              {/* Gold sweep */}
              <div 
                className="absolute inset-0 bg-gradient-to-t from-gold-400/40 via-gold-400/20 to-transparent" 
                style={{ 
                  clipPath: 'polygon(50% 50%, 100% 0, 0 0)',
                  animation: 'radar-sweep 4s linear infinite',
                  transformOrigin: 'bottom center'
                }}
              />
              
              {/* Green sweep (delayed) */}
              <div 
                className="absolute inset-0 bg-gradient-to-t from-emerald-500/40 via-emerald-500/20 to-transparent" 
                style={{ 
                  clipPath: 'polygon(50% 50%, 100% 0, 0 0)',
                  animation: 'radar-sweep 4s linear infinite 2s',
                  transformOrigin: 'bottom center'
                }}
              />
            </div>
            
            {/* Center icon with glow */}
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <motion.div 
               className="backdrop-blur-md p-5 rounded-full w-32 h-32 flex items-center justify-center bg-transparent border-transparent"


                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{ animation: 'glow 3s ease-in-out infinite' }}
              >
                <img
                  src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png"
                  alt="Playclub Logo" 
                  className="h-20 w-20"
                  style={{
                    animation: 'spin 3s linear infinite',
                    animationPlayState: 'running'
                  }}
                />
              </motion.div>
            </div>
            
            {/* Location pins */}
            {[1, 2, 3].map((i) => (
              <motion.div
                key={`pin-${i}`}
                className="absolute"
                initial={{ 
                  x: (Math.random() - 0.5) * 400, 
                  y: (Math.random() - 0.5) * 400,
                  scale: 0,
                  opacity: 0
                }}
                animate={{ 
                  scale: [0, 1.2, 1],
                  opacity: [0, 1, 0.7, 0]
                }}
                transition={{ 
                  duration: 2,
                  delay: i * 0.8,
                  repeat: Infinity,
                  repeatDelay: 3
                }}
              >
                <MapPin className={cn(
                  "h-6 w-6",
                  i % 3 === 0 ? "text-gold-400" : 
                  i % 3 === 1 ? "text-emerald-500" : "text-blue-500"
                )} />
              </motion.div>
            ))}
            
            {/* Floating player avatars */}
            <AnimatePresence>
              {playerAvatars.map((avatar, i) => (
                <motion.div
                  key={`avatar-${i}`}
                  className="absolute w-14 h-14 rounded-full overflow-hidden"
                  initial={{ 
                    x: (Math.random() - 0.5) * 400, 
                    y: (Math.random() - 0.5) * 400,
                    opacity: 0,
                    scale: 0
                  }}
                  animate={{ 
                    x: (Math.random() - 0.5) * 150,
                    y: (Math.random() - 0.5) * 150,
                    opacity: [0, 1, 0],
                    scale: [0, 1, 0]
                  }}
                  transition={{ 
                    duration: 3,
                    delay: i * 0.5,
                    repeat: 1,
                    repeatDelay: 0.5
                  }}
                  style={{
                    boxShadow: i % 2 === 0 
                      ? '0 0 15px rgba(255, 201, 51, 0.6)' 
                      : '0 0 15px rgba(16, 185, 129, 0.6)'
                  }}
                >
                  <img 
                    src={avatar}
                    alt="Player"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.currentTarget;
                      target.src = "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png";
                      target.onerror = null; // Prevent infinite error loop
                    }}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          
          {/* Text content with gradient */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="relative z-10"
          >
            <h3 className="text-2xl font-bold mb-3 mt-4 bg-gradient-to-r from-gold-400 via-emerald-400 to-blue-400 text-transparent bg-clip-text">
              Finding Players Near You
            </h3>
            <p className="text-lg text-white flex items-center justify-center mb-2">
              <Search className="h-5 w-5 mr-2 text-gold-400 animate-pulse" />
          <span className="!text-white opacity-80">Scanning your area...</span>

            </p>
            <p className="text-sm text-white/50">
              
            </p>
          </motion.div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}